import { useState } from 'preact/hooks';
import { useAuth } from '../../context/AuthContext.js';
import { api } from '../../services/api.js';
import { PHONE_INVALID_MESSAGE, PHONE_RE } from '../../utils/validation.js';
import { Button } from './Button.js';

/**
 * 155 (#19): the one field a Google account never had.
 *
 * Registration by email has required a phone number since 140 (`auth/schemas.ts:35`); Google
 * sign-up creates the account from the Google profile alone (`auth/google.ts:90`), so every
 * customer who arrived that way has none and nothing ever asked them.
 *
 * Shown by `AuthGuard` in place of a protected screen whenever the signed-in account has no phone.
 * The condition is the MISSING FIELD, never "signed up with Google" — which is what makes existing
 * accounts self-heal on their next sign-in, with nothing run against the database.
 *
 * Validation uses the app's one phone rule (`utils/validation.ts`), the same one email registration
 * enforces, so the two paths cannot drift into disagreeing about what a phone number is.
 */
export function PhoneRequired() {
  const { logout, refreshUser } = useAuth();
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function save(e: Event) {
    e.preventDefault();
    const trimmed = phone.trim();

    if (!trimmed || !PHONE_RE.test(trimmed)) {
      setError(PHONE_INVALID_MESSAGE);
      return;
    }

    setSaving(true);
    setError('');
    try {
      await api.patch('/users/me', { phone: trimmed });
      // The guard re-derives the condition from the account, so the app has to re-read it —
      // otherwise a successful save leaves the customer looking at this screen forever.
      await refreshUser();
    } catch {
      // Stated, and the control stays usable. A required screen whose save fails silently is a
      // locked account (FR-006).
      setError('Could not save your phone number. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div data-testid="phone-required" className="page-fill bg-bg px-4 pt-10">
      <h1 className="text-xl font-bold text-text mb-2">One more thing</h1>
      <p className="text-sm text-gray-400 mb-6">
        We need a phone number so we can reach you about your rental.
      </p>

      <form onSubmit={save} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <label htmlFor="required-phone" className="text-sm text-gray-400">
            Phone number
          </label>
          <input
            id="required-phone"
            data-testid="phone-required-input"
            type="tel"
            name="phone"
            autoComplete="tel"
            value={phone}
            onInput={(e) => setPhone((e.target as HTMLInputElement).value)}
            className="rounded-lg bg-surface-card px-3 py-2 text-text outline-none"
          />
          {error && <p className="text-sm text-red-400">{error}</p>}
        </div>

        <Button type="submit" loading={saving} size="lg" className="w-full">
          Save
        </Button>
      </form>

      {/* FR-007: a required screen with no way out is a locked account. */}
      <button
        type="button"
        onClick={() => logout()}
        className="mt-6 w-full text-sm text-gray-400 underline"
      >
        Sign out
      </button>
    </div>
  );
}
